import {
  ButtonFacetedFilter,
  FacetedFilterOption,
} from "@/components/button-faceted-filter";
import MapCompanies from "@/components/company/map-companies";
import DataTableCompanies from "@/components/data-table-companies/data-table-companies";
import DataTableCompanyEmployees from "@/components/data-table-company-employees/data-table-company-employees";
import {
  MapControls,
  MapControlsItineraryState,
} from "@/components/itinerary/map-controls";
import DialogAssignListing from "@/components/listing/dialog-assign-listing";
import DialogUpdateListing from "@/components/listing/dialog-update-listing";
import PageSkeleton from "@/components/page-skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { getTagsList } from "@/lib/tags";
import { setWindowTitle } from "@/lib/window-title";
import { useUser } from "@clerk/react";
import {
  CompanyDTO,
  EmployeeDTO,
  ListingDetailDTO,
  ListingWithCompaniesDTO,
  ListingWithEmployeesDTO,
} from "@shaarp/common";
import { PencilIcon, UserPlusIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

export default function ListingContentPage() {
  const toastError = useToastError();
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useUser();
  const { isManager, isAdmin, isAdvisor } = useTeams();
  const { isAdminMode } = useAdminMode();
  const { t } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();

  const { listingId } = useParams();
  const { apiFetchListings, apiFetchCollaboratorsListings } = useListings();

  const [dialogUpdateListing, setDialogUpdateListing] =
    useState<boolean>(false);
  const [dialogAssignListing, setDialogAssignListing] =
    useState<boolean>(false);
  const [listing, setListing] = useState<ListingDetailDTO>();
  const [companies, setCompanies] = useState<CompanyDTO[]>([]);
  const [employees, setEmployees] = useState<EmployeeDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMapOpened, setIsMapOpened] = useState<boolean>(false);

  const [companySelected, setCompanySelected] = useState<
    CompanyDTO | undefined
  >(undefined);

  const [itineraryState, setItineraryState] =
    useState<MapControlsItineraryState | null>(null);

  // Ref pour désactiver l'itinéraire lors du changement de listing
  const itineraryStateRef = useRef<MapControlsItineraryState | null>(null);

  useEffect(() => {
    itineraryStateRef.current = itineraryState;
  }, [itineraryState]);

  const [tagsSelected, setTagsSelected] = useState<string[]>([]);
  const [tagsOptions, setTagsOptions] = useState<FacetedFilterOption[]>([]);

  const companiesListing =
    listing?.listingType === "companies"
      ? (listing as ListingWithCompaniesDTO)
      : undefined;
  const employeesListing =
    listing?.listingType === "employees"
      ? (listing as ListingWithEmployeesDTO)
      : undefined;

  useEffect(() => {
    const tagsList = getTagsList(t);
    setTagsOptions(
      tagsList.map((tag) => ({
        label: tag.label,
        value: tag.value,
        total:
          companiesListing?.companies?.filter(
            (c) => c.company.assignation?.tag === tag.value
          ).length || 0,
      }))
    );
  }, [listing, t]);

  useEffect(() => {
    setCompanies(
      companiesListing?.companies
        ?.filter(
          (c) =>
            tagsSelected.length === 0 ||
            tagsSelected.includes(c.company.assignation?.tag || "")
        )
        .map((c) => c.company) || []
    );
  }, [listing, tagsSelected]);

  useEffect(() => {
    setEmployees(employeesListing?.employees?.map((e) => e.employee) || []);
  }, [listing]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<ListingDetailDTO>(`/listing/${listingId}`);
      setListing(data);
    } catch (error: unknown) {
      const name = error instanceof Error ? error.name : undefined;
      const code =
        typeof error === "object" && error !== null && "code" in error
          ? (error as { code?: unknown }).code
          : undefined;
      if (name !== "AbortError" && code !== "ERR_CANCELED") {
        toastError();
        setListing(undefined);
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, listingId, toastError]);

  useEffect(() => {
    if (listingId) {
      fetchData();
    }
  }, [listingId]);

  useEffect(() => {
    setWindowTitle(
      listing
        ? t("listing.page.content.windowTitle", { title: listing.title })
        : t("listing.page.content.defaultWindowTitle")
    );
  }, [listing]);

  const onListingUpdated = async () => {
    setDialogUpdateListing(false);
    fetchData();
  };

  const onListingRemoved = async () => {
    setDialogUpdateListing(false);
    navigate("/listing");
  };

  const getDateCompanyAdded = (company: CompanyDTO) => {
    const link = companiesListing?.companies?.find(
      (c) => c.company.id === company.id
    );
    const date = link?.date;

    return date ? new Date(date).toLocaleString() : "";
  };

  const removeCompanyFromListing = async (company: CompanyDTO) => {
    try {
      await api.delete(`/listing/${listingId}/companies`, {
        data: { companiesIds: [company.id] },
      });
      setListing({
        ...listing,
        companies: companiesListing?.companies?.filter(
          (c) => c.company.id !== company.id
        ),
      } as ListingWithCompaniesDTO);
      apiFetchListings({ force: true });
      toast({
        variant: "default",
        title: t("listing.page.content.actions.removeCompany.success.title"),
        description: t(
          "listing.page.content.actions.removeCompany.success.description",
          { name: company.name }
        ),
      });
    } catch (error) {
      toastError();
    }
  };

  const canEditListing = () => {
    const isAuthor = listing?.author?.id === user?.id;
    const isAssignee = listing?.assignees?.some((a) => a.id === user?.id);
    const canManageTeamListings = isAdmin || isManager || isAdvisor;
    return (
      (listing?.ownerType === "user" &&
        (isAuthor || isAssignee || canManageTeamListings)) ||
      (listing?.ownerType === "shaarp" && isAdminMode)
    );
  };

  const isCompaniesListing = listing?.listingType === "companies";
  const isEmployeesListing = listing?.listingType === "employees";

  const removeEmployeeFromListing = async (employee: EmployeeDTO) => {
    try {
      await api.delete(`/listing/${listingId}/employees`, {
        data: { employeesIds: [employee.id] },
      });
      setListing({
        ...listing,
        employees: employeesListing?.employees?.filter(
          (e) => e.employee.id !== employee.id
        ),
      } as ListingWithEmployeesDTO);
      apiFetchListings({ force: true });
      toast({
        variant: "default",
        title: t("listing.page.content.actions.removeEmployee.success.title"),
        description: t(
          "listing.page.content.actions.removeEmployee.success.description",
          {
            name: [employee.firstName, employee.lastName]
              .filter(Boolean)
              .join(" "),
          }
        ),
      });
    } catch (error) {
      toastError();
    }
  };

  // A team whose contact switch is off sees no key contacts (#757): a contact list
  // opened from a link sends it back to its lists.
  if (employeesListing && !arePeopleVisible) {
    return <Navigate to="/listing" replace />;
  }

  return (
    <>
      <div className="absolute top-0 left-0 flex-1 w-full h-[calc(100%)] overflow-auto">
        <ResizablePanelGroup orientation="horizontal" className="w-full">
          <ResizablePanel className="relative">
            <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
              <div className="flex-1 space-y-5">
                {isLoading && <PageSkeleton />}

                {!isLoading && (
                  <>
                    <div className="flex justify-between">
                      <div className="space-y-2">
                        <h2 className="text-2xl font-bold tracking-tight">
                          {listing ? (
                            <>
                              {listing?.title}
                              {canEditListing() && (
                                <Button
                                  variant="link"
                                  onClick={() => setDialogUpdateListing(true)}
                                  className="text-gray-400 transition-colors hover:text-gray-700"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          ) : (
                            t("listing.page.content.selectList")
                          )}
                        </h2>
                        {listing?.description && (
                          <p className="text-gray-500">
                            {listing?.description}
                          </p>
                        )}
                        {listing && (isManager || isAdvisor) && (
                          <div className="flex gap-2 items-center">
                            {listing.assignees &&
                              listing.assignees.length > 0 && (
                                <div className="flex gap-2 items-center">
                                  <span className="text-xs text-muted-foreground">
                                    {t("listing.page.list.assignedTo")}
                                  </span>
                                  <div className="flex -space-x-1">
                                    {listing.assignees.slice(0, 5).map((a) => (
                                      <Tooltip key={a.id}>
                                        <TooltipTrigger asChild>
                                          <Avatar className="w-6 h-6 border border-background">
                                            <AvatarImage src={a.imageUrl} />
                                            <AvatarFallback>
                                              {a.fullName?.charAt(0)}
                                            </AvatarFallback>
                                          </Avatar>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {a.fullName}
                                        </TooltipContent>
                                      </Tooltip>
                                    ))}
                                  </div>
                                </div>
                              )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDialogAssignListing(true)}
                            >
                              <UserPlusIcon className="mr-2 w-4 h-4" />
                              {t("listing.page.list.assignButton")}
                            </Button>
                          </div>
                        )}
                      </div>

                      {listing && isCompaniesListing && (
                        <MapControls
                          companies={companies}
                          isMapOpened={isMapOpened}
                          onMapOpenChange={setIsMapOpened}
                          onItineraryStateChange={setItineraryState}
                        />
                      )}
                    </div>

                    {listing && isCompaniesListing && (
                      <DataTableCompanies
                        companies={companies}
                        selected={companySelected}
                        onSortedDataChange={itineraryState?.setSortedCompanies}
                        overrideColumnsVisibility={["Tag", "Assignation"]}
                        columnsPersistenceKey="data-table-companies-listing-content-v1"
                        enabledListingFeature={true}
                        additionalFilters={
                          <ButtonFacetedFilter
                            title="Tags"
                            compacted={false}
                            options={tagsOptions}
                            selected={tagsSelected}
                            onChange={setTagsSelected}
                          />
                        }
                        additionalColumns={
                          listing?.ownerType === "shaarp" && !isAdminMode
                            ? []
                            : [
                                [
                                  t("listing.page.content.columns.dateAdded"),
                                  getDateCompanyAdded,
                                ],
                                [
                                  t("listing.page.content.columns.remove"),
                                  (company: CompanyDTO) => (
                                    <Button
                                      variant="link"
                                      className="transition-colors hover:text-red-700"
                                      onClick={() =>
                                        removeCompanyFromListing(company)
                                      }
                                    >
                                      {t("listing.page.content.columns.remove")}
                                    </Button>
                                  ),
                                ],
                              ]
                        }
                        sortingState={
                          listing?.ownerType === "shaarp"
                            ? [
                                {
                                  id: "Nom",
                                  desc: false,
                                },
                              ]
                            : [
                                {
                                  id: t(
                                    "listing.page.content.columns.dateAdded"
                                  ),
                                  desc: true,
                                },
                              ]
                        }
                      />
                    )}

                    {listing && isEmployeesListing && (
                      <DataTableCompanyEmployees
                        employees={employees}
                        pagination={true}
                        showTotalCounter={true}
                        onRemoveFromListing={
                          listing?.ownerType === "shaarp" && !isAdminMode
                            ? undefined
                            : removeEmployeeFromListing
                        }
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </ResizablePanel>
          {isMapOpened && (
            <>
              <ResizableHandle
                withHandle
                className="transition-all hover:bg-gray-500 active:bg-gray-200"
              />
              <ResizablePanel defaultSize="20%" maxSize="50%">
                <MapCompanies
                  companies={itineraryState?.companiesForMap || companies}
                  selected={companySelected}
                  setSelected={setCompanySelected}
                  shouldCalculateItinerary={
                    itineraryState?.shouldCalculateItinerary || false
                  }
                />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </div>

      {dialogUpdateListing && listing && (
        <DialogUpdateListing
          listing={listing}
          onClose={() => setDialogUpdateListing(false)}
          onUpdated={onListingUpdated}
          onRemoved={onListingRemoved}
        />
      )}

      {dialogAssignListing && listing && (
        <DialogAssignListing
          listing={listing}
          onClose={() => setDialogAssignListing(false)}
          onAssigned={() => {
            setDialogAssignListing(false);
            fetchData();
            apiFetchListings({ force: true });
            apiFetchCollaboratorsListings({ force: true });
          }}
        />
      )}
    </>
  );
}
